import { AppointmentApiService } from '@api_patient/appointment_api.service';
import { GeneralApiService } from '@api_patient/general_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import { isHttpError } from '@application/http_error';
import { UniversalConfig } from '@application/universal_config.service';
import { CellulaFormSubmitData } from '@cellula/react';
import { FileManagerService } from '@feature/file_manager/mod';
import { useTimeSlots } from '@feature/time_slot/use_time_slots';
import type { PhysicalLocationApiModel, TimeslotApiModel } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BookingScreen, useBooking, ViewBookingAnalyticActions, type ViewBookingContextValue } from '../../hooks/use_booking';
import { type BookingBasketHook, CommitResult, useBookingBasket } from '../../hooks/use_booking_basket';

interface AsyncState<T> {
	value: T | null;
	status: 'pending' | 'accessible' | 'error';
}

interface PresentationModel {
	requirePayment: boolean;
	timeSlot: TimeslotApiModel;
	physicalLocation: PhysicalLocationApiModel | null;
	reasonForVisit: string | null;
	asAgentFor: string | null;
}

export class SlotNotAvailableError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'SlotNotAvailableError';
		Object.setPrototypeOf(this, SlotNotAvailableError.prototype);
	}
}

export class TooCloseToAppointmentStartError extends Error {
	constructor(public message: string) {
		super(message);
		this.name = 'TooCloseToAppointmentStartError';
		Object.setPrototypeOf(this, TooCloseToAppointmentStartError.prototype);
	}
}

export function useScreenConclusion() {
	const viewBooking = useBooking();
	const analyticService = useResolve(AnalyticService);
	const featureUseRepresentatives = useFeatureFlag('PatientWeb.UseRepresentatives');
	const bookingBasket = useBookingBasket();
	const universalConfig = useResolve(UniversalConfig);
	const timeSlotsApi = useTimeSlots();
	const appointmentApiService = useResolve(AppointmentApiService);
	const generalApiService = useResolve(GeneralApiService);
	const fileManagerService = useResolve(FileManagerService);

	const [data, setData] = useState<AsyncState<PresentationModel>>({ value: null, status: 'pending' });
	const [termsOfServiceUrl, setTermsOfServiceUrl] = useState<string | null>(null);
	const [showUnavailableTimeSlotDialog, setShowUnavailableTimeSlotDialog] = useState(false);
	const ticketRef = useRef<string | null>(null);
	const basketRef = useRef(bookingBasket.getBasket());

	const fetchTimeSlot = useCallback(async () => {
		let timeSlot: TimeslotApiModel;
		try {
			timeSlot = await timeSlotsApi.getTimeSlot(universalConfig.selectedUnitId, basketRef.current?.timeSlotId ?? '');
		} catch {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.ErrorFetchingTimeSlot);
			viewBooking.goToScreen(BookingScreen.Error);
			return;
		}

		let physicalLocation: PhysicalLocationApiModel | null = null;
		if (timeSlot.PhysicalLocationId) {
			physicalLocation =
				(viewBooking.flowData.status === 'accessible'
					? viewBooking.flowData.value.physicalLocations.find((location) => location.Id === timeSlot.PhysicalLocationId)
					: null) ?? null;
		}

		setData({
			value: {
				requirePayment: timeSlot.RequiresPayment,
				timeSlot,
				physicalLocation,
				reasonForVisit: viewBooking.getSelectedReasonForVisit()?.Name ?? null,
				asAgentFor: basketRef.current?.asAgentFor ?? null,
			},
			status: 'accessible',
		});
	}, [analyticService, timeSlotsApi, universalConfig.selectedUnitId, viewBooking]);

	const rescheduleAppointment = useCallback(
		async (rescheduleTicket: string, newTimeslotId: string): AsyncResult<CommitResult> => {
			const basket = bookingBasket.getBasket();
			const reschedulingReason = basket.reschedulingReason || undefined;

			try {
				const appointment = await timeSlotsApi.rebookTimeSlot(
					universalConfig.selectedUnitId,
					rescheduleTicket,
					newTimeslotId,
					reschedulingReason,
				);

				await bookingBasket.setBasket({ reschedulingReason: null });

				ticketRef.current = appointment.Ticket;

				return Ok({
					ticket: appointment.Ticket,
					payment: null,
				});
			} catch (error) {
				await bookingBasket.setBasket({ reschedulingReason: null });

				if (isHttpError<{ ErrorCode: string }>(error)) {
					if (error.data?.ErrorCode === 'SlotNotAvailable') {
						analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.SelectedTimeSlotNotIsAvailable);
						setShowUnavailableTimeSlotDialog(true);
						return Err(new SlotNotAvailableError('Selected time slot is not available'));
					}
				}
				return Err(error instanceof Error ? error : new Error(String(error)));
			}
		},
		[analyticService, bookingBasket, timeSlotsApi, universalConfig.selectedUnitId],
	);

	const submit = useCallback(
		async (formData: CellulaFormSubmitData): AsyncResult<CommitResult> => {
			const flowSettings = viewBooking.getFlowSettings();
			const basket = bookingBasket.getBasket();

			if (basket.rescheduleTicket && basket.timeSlotId) {
				return rescheduleAppointment(basket.rescheduleTicket, basket.timeSlotId);
			}

			const reason = flowSettings.descriptionOfMatter ?? formData.reason ?? '';
			const fileTickets = fileManagerService.peakUploadedIdentities();

			const commitResult = await bookingBasket.commitBasket({
				reason,
				files: fileTickets,
				redRobinSessionId: flowSettings.redRobinSessionId ?? undefined,
			});
			if (commitResult.err) {
				if (isHttpError<{ ErrorCode: string }>(commitResult.err)) {
					if (commitResult.err.data?.ErrorCode === 'SlotNotAvailable') {
						analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.SelectedTimeSlotNotIsAvailable);
						setShowUnavailableTimeSlotDialog(true);
						return Err(new SlotNotAvailableError('Selected time slot is not available'));
					} else if (commitResult.err.data?.ErrorCode === 'TooCloseToAppointmentStart') {
						return Err(new TooCloseToAppointmentStartError('Too close to appointment start'));
					}
				}
				return Err(commitResult.err);
			}

			ticketRef.current = commitResult.ok.ticket;
			return Ok(commitResult.ok);
		},
		[analyticService, bookingBasket, fileManagerService, rescheduleAppointment, viewBooking],
	);

	const abortPayment = useCallback(async () => {
		if (!ticketRef.current) return;

		const cancelResult = await appointmentApiService.cancel(ticketRef.current);
		if (cancelResult.err) {
			viewBooking.goToScreen(BookingScreen.Error);
			return;
		}

		await bookingBasket.setBasket({
			timeSlotId: cancelResult.ok.data.NewTimeslot?.Id ?? null,
		});
		basketRef.current = bookingBasket.getBasket();
		await fetchTimeSlot();
	}, [appointmentApiService, bookingBasket, fetchTimeSlot, viewBooking]);

	useEffect(() => {
		analyticService.trackPageView({ customTitle: 'Booking: Conclusion' });

		const basket = basketRef.current;
		const flowData = viewBooking.flowData.status === 'accessible' ? viewBooking.flowData.value : null;

		const allowedToBeHere = (() => {
			if (basket == null || !flowData) return false;
			if (basket.rescheduleTicket) return basket.timeSlotId != null;
			if (
				flowData.useReasonForVisits &&
				viewBooking.getFilteredReasonForVisits().length > 0 &&
				!viewBooking.getSelectedReasonForVisit()
			) {
				return false;
			}
			return basket.timeSlotId != null;
		})();

		if (!allowedToBeHere) {
			analyticService.trackEvent(AnalyticCategories.Booking, ViewBookingAnalyticActions.NotAllowedToBeHere, 'Conclusion');
			viewBooking.goToScreen(featureUseRepresentatives ? BookingScreen.Access : BookingScreen.Forms);
			return;
		}

		fetchTimeSlot();

		(async () => {
			const policyDocumentsResult = await generalApiService.getPolicyDocuments(
				universalConfig.alias,
				universalConfig.selectedLanguageCode.peek(),
			);
			if (policyDocumentsResult.ok) {
				setTermsOfServiceUrl(policyDocumentsResult.ok.data.TermsOfServiceUrl);
			}
		})();
	}, [analyticService, fetchTimeSlot, generalApiService, universalConfig, viewBooking, featureUseRepresentatives]);

	return { data, termsOfServiceUrl, showUnavailableTimeSlotDialog, submit, abortPayment };
}
